// THE ACADEMY (v245, first-run plan v3 §4). Opt-in, interactive lessons on the REAL engine, never pushed.
//
//   initAcademy(ctx)  ctx: { launch(lesson, runner), openGuide(), replayPrologue(), report(lessonId) -> Promise<{packGranted}> }
//   openHall()        the hall: 4 lesson tiles, the Prologue replay, the old card guide
//   glint(on)         the one gentle hint (the Academy pill's orbiting glint; see wantGlint)
//
// A lesson is a level record (data/academy/lessons.json: existing knobs + its patternSeed, which alone
// scripts the foe's intents) played with a loaner squad, in PRACTICE: main.js launches it with
// driver.practice + driver.academy (this runner); barrage.js suppresses the first-time cues, skips the
// trace log, and awaits runner.hold(beat) at three places (before the swap window, before the block pad,
// after a stamp). main.js's practice branch writes no record, reward, XP or campaign progress. The only
// thing a lesson stores is localStorage `nomadmon-academy`. Words on screen: <= 5 per beat, numbers read
// live from BE.CARD_RULES. All four lessons done = +1 pack, once per account (server /academy/clear).
import * as BE from './barrage-engine.js';
import * as audio from './audio.js';
import { ghostPath, stopGhost } from './onboard.js';
import DATA from '../data/academy/lessons.json' with { type: 'json' };

const KEY = 'nomadmon-academy';
const GLINT_AFTER = 5;          // completed matches before the one gentle hint
const GLINT_VISITS = 3;         // squad-screen visits that clear it unopened
let C = null;

function prog() {
  let p = null;
  try { p = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch { p = null; }
  return p && typeof p === 'object' ? { done: p.done || {}, sent: p.sent || {}, opened: !!p.opened, visits: p.visits | 0, paid: !!p.paid } : { done: {}, sent: {}, opened: false, visits: 0, paid: false };
}
function save(p) { try { localStorage.setItem(KEY, JSON.stringify(p)); } catch { /* private mode */ } }
export const lessons = () => DATA.lessons;
export function initAcademy(ctx) { C = ctx; ensureCss(); expose(); }

// ------------------------------------------------------------------ the one gentle hint
/** The pill wears the glint after the 5th completed match until the hall is opened or 3 visits pass. */
export function wantGlint(matches) { const p = prog(); return matches >= GLINT_AFTER && !p.opened && p.visits < GLINT_VISITS; }
export function noteVisit(matches) { if (matches < GLINT_AFTER) return; const p = prog(); if (!p.opened && p.visits < GLINT_VISITS) { p.visits++; save(p); } }

// ------------------------------------------------------------------ the hall
let hallEl = null;
export function openHall() {
  ensureCss();
  const p = prog();
  if (!p.opened) { p.opened = true; save(p); }
  closeHall();
  const el = document.createElement('div');
  el.className = 'ac-hall';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-label', 'Academy');
  const tile = (L, i) => `<button type="button" class="ac-tile${p.done[L.id] ? ' done' : ''}" data-l="${L.id}"><b>${i + 1}</b><span>${L.title}<small>${L.goal}</small></span>${p.done[L.id] ? '<i>✓</i>' : ''}</button>`;
  el.innerHTML = `<div class="ac-box"><button type="button" class="ac-x" aria-label="Close">✕</button><h2>Academy</h2>
    <div class="ac-tiles">${DATA.lessons.map(tile).join('')}</div>
    <p class="ac-pay">${p.paid ? 'Graduated' : 'All four: +1 pack'}</p>
    <div class="ac-row"><button type="button" class="ac-pro">▶ Prologue</button><button type="button" class="ac-guide">Card guide</button></div></div>`;
  el.addEventListener('click', (e) => {
    const t = e.target.closest('.ac-tile');
    if (t) { tick(); closeHall(); start(t.dataset.l); return; }
    if (e.target.closest('.ac-x') || e.target === el) { tick(); closeHall(); return; }
    if (e.target.closest('.ac-pro')) { tick(); closeHall(); C && C.replayPrologue && C.replayPrologue(); return; }
    if (e.target.closest('.ac-guide')) { tick(); closeHall(); C && C.openGuide && C.openGuide(); }
  });
  document.body.appendChild(el);
  hallEl = el;
  requestAnimationFrame(() => el.classList.add('on'));
  // econfix MIGRATION: lessons finished before the server kept a ledger (or while offline) are
  // re-sent once from here; the server pays the graduation pack at most once per account
  if (Object.keys(p.done).some(id => !p.sent[id])) {
    sendProgress().then((paid) => {
      const pay = hallEl === el && el.querySelector('.ac-pay');
      if (paid && pay) pay.textContent = 'Graduated · +1 pack';
    }).catch(() => {});
  }
}
export function closeHall() { if (hallEl) { hallEl.remove(); hallEl = null; } }
const tick = () => { try { audio.play('tick', { gain: 0.5 }); } catch { /* */ } };

// ------------------------------------------------------------------ a lesson run
let R = null;     // the live runner
function start(id) {
  const L = DATA.lessons.find(x => x.id === id);
  if (!L || !C || !C.launch) return;
  R = makeRunner(L);
  C.launch({ lesson: L, squad: DATA.squad.slice(), foe: DATA.foe.slice(), attemptSeed: (Date.now() ^ (Math.random() * 1e9)) >>> 0 }, R);
}
/** Retry: the same lesson, a fresh attempt seed (the patternSeed keeps the AI beats the same). */
export function retry() { if (R) start(R.lesson.id); }

const RULES = () => BE.CARD_RULES;
const x100 = (v) => `×${(v / 100).toFixed(v % 100 ? (v % 10 ? 2 : 1) : 0)}`;
function makeRunner(L) {
  const shown = new Set();
  const st = { met: false, why: null, holds: [], evs: 0 };
  const PS = 'A', ES = 'B';
  // the beat for this site now, or null. Each line shows once per run.
  const lineFor = (site, S, info) => {
    const E = S.st.sides[ES], P = S.st.sides[PS], nx = E.intentNext, cur = E.intent, R_ = RULES();
    const canSwap = P.chars.some((c, i) => i !== P.active && BE.canSwap(S.st, PS, i));
    if (L.id === 'A1') {
      if (site === 'swap' && nx === 'CHARGE') return ['a1-charge', `⚡ Charge next: ${x100(R_.intent.chargeNext)}`];
      if (site === 'block' && cur === 'CHARGE') return ['a1-block', 'Block the charged hit'];
      if (site === 'swap' && nx === 'SHIELD') return ['a1-shield', '⛨ Shield next: GREAT+ breaks'];
    } else if (L.id === 'A2') {
      if (site === 'swap' && P.pips > 0 && canSwap) return ['a2-pip', 'Pip! Tap a glowing card', 'tap'];
      if (site === 'swap' && nx === 'CHARGE') return ['a2-charge', '⚡ Charge next: swap out'];
      if (site === 'stamp' && !info.closer && info.level >= 2 && P.pips === 0) return ['a2-great', 'Bench GREAT = a pip'];
    } else if (L.id === 'A3') {
      if (site === 'swap') { const me = BE.activeOf(S.st, PS), foe = BE.activeOf(S.st, ES); if (BE.typePct(me.type, foe.type) > 100) return ['a3-type', `${me.type} beats ${foe.type}: ${x100(R_.type.adv)}`]; }
    } else if (L.id === 'A4') {
      if (site === 'stamp' && info.closer && info.level >= 2) return ['a4-sig', info.level === 3 ? `PERFECT: signature ${x100(R_.perfectX * 100)}` : 'GREAT closer: signature fires'];
      if (site === 'swap') return ['a4-cap', `Captain: +${R_.pool.captain}% power`];
    }
    return null;
  };
  return {
    lesson: L,
    hold(site, S, info = {}) {
      const ln = lineFor(site, S, info);
      if (!ln || shown.has(ln[0])) return Promise.resolve();
      shown.add(ln[0]);
      st.holds.push(ln[0]);
      return holdCard(ln[1], ln[2] === 'tap');
    },
    ev(list, S) {
      for (const e of list || []) {
        st.evs++;
        if (L.challenge === 'shatter' && e.ev === 'kw' && e.role === 'intent' && e.fx === 'shatter' && e.side === PS) met('shatter');
        if (L.challenge === 'swap' && e.ev === 'swap' && e.side === PS) met('swap');
        if (L.challenge === 'perfectsig' && e.ev === 'kw' && e.role === 'sig' && e.side === PS && (e.x | 0) >= RULES().perfectX) met('perfect signature');
      }
    },
    /** at the result: did the lesson's challenge happen? (A3: a win with the counter type landing) */
    met(won, kst) {
      if (L.challenge === 'typewin' && won && kst && (kst.adv | 0) > 0) met('type win');
      return st.met;
    },
    // the challenge is met AND every scripted beat has played (st.round counts the rounds finished)
    complete: (S) => st.met && (!S || S.st.round >= ((L.ai && L.ai.intent) || []).length),
    debug: () => ({ id: L.id, met: st.met, why: st.why, holds: st.holds.slice(), evs: st.evs, holding: !!holdEl }),
  };
  function met(why) { if (!st.met) { st.met = true; st.why = why; } }
}

// one tap to continue: a short line in a pill over the fight, a fingertip on the bench when it is a tap beat
let holdEl = null;
function holdCard(text, tapBench) {
  ensureCss();
  return new Promise((resolve) => {
    const el = document.createElement('div');
    el.className = 'ac-hold';
    el.setAttribute('role', 'status');
    el.innerHTML = `<p>${text}</p><small>tap</small>`;
    document.body.appendChild(el);
    holdEl = el;
    requestAnimationFrame(() => el.classList.add('on'));
    if (tapBench) {
      const th = document.querySelector('#barrage .bg-th.can') || document.querySelector('#barrage .bg-rail.me .bg-th');
      const r = th && th.getBoundingClientRect();
      if (r && r.width > 4) ghostPath([{ x: (r.left + r.right) / 2, y: (r.top + r.bottom) / 2 }], { ms: 1200 });
    }
    const t0 = performance.now();
    const go = (e) => {
      if (performance.now() - t0 < 300) return;
      e.preventDefault(); e.stopPropagation();
      removeEventListener('pointerup', go, true);
      stopGhost();
      el.classList.remove('on');
      setTimeout(() => el.remove(), 250);
      holdEl = null;
      resolve();
    };
    addEventListener('pointerup', go, true);
  });
}

// ------------------------------------------------------------------ the result
/** main.js calls this when a practice match ends. res: { won, kst }. Shows the outcome; resolves when dismissed. */
export async function finish(res) {
  const r = R;
  if (!r) return { met: false };
  const met = r.met(!!(res && res.won), res && res.kst);
  let paid = 0;
  if (met) {
    const p = prog();
    p.done[r.lesson.id] = p.done[r.lesson.id] || Date.now();
    save(p);
    paid = await sendProgress();
  }
  if (window.__nm) window.__nm.academyLast = { id: r.lesson.id, met, paid };
  return new Promise((resolve) => {
    const el = document.createElement('div');
    el.className = 'ac-result' + (met ? ' won' : '');
    el.innerHTML = `<div class="ac-box"><h2>${met ? 'Lesson complete' : 'Not yet'}</h2><p>${met ? r.lesson.title : r.lesson.goal}</p>
      ${paid ? '<p class="ac-pay">+1 pack</p>' : ''}
      <div class="ac-row">${met ? '' : '<button type="button" class="ac-retry">Retry</button>'}<button type="button" class="ac-back">Academy</button></div></div>`;
    el.addEventListener('click', (e) => {
      if (e.target.closest('.ac-retry')) { tick(); el.remove(); resolve({ met, paid, next: 'retry' }); }
      else if (e.target.closest('.ac-back')) { tick(); el.remove(); resolve({ met, paid, next: 'hall' }); }
    });
    document.body.appendChild(el);
    requestAnimationFrame(() => el.classList.add('on'));
  });
}
// every completed lesson not yet confirmed by the server (a failed post is re-sent with the next one)
async function sendProgress() {
  if (!C || !C.report) return 0;
  const p = prog();
  let paid = 0;
  for (const id of Object.keys(p.done)) {
    if (p.sent[id]) continue;
    try {
      const a = await C.report(id);
      if (!a || a.error) continue;
      if (!a.local) p.sent[id] = 1;
      if (a.packGranted) { paid = 1; p.paid = true; }
    } catch { /* offline: re-sent next time */ }
  }
  save(p);
  return paid;
}

/** QA handle (main.js puts it on window.__nm, which it replaces wholesale at boot). */
export const api = { open: openHall, close: closeHall, start: (id) => start(id), retry, state: () => ({ ...prog(), live: R ? R.debug() : null }), lessons: () => DATA.lessons.map(l => l.id) };
function expose() { const nm = (window.__nm = window.__nm || {}); nm.academy = api; }

// ------------------------------------------------------------------ css
function ensureCss() {
  if (document.getElementById('ac-css')) return;
  const s = document.createElement('style');
  s.id = 'ac-css';
  s.textContent = `
.ac-hall,.ac-result{position:fixed;inset:0;z-index:8000;display:flex;align-items:center;justify-content:center;padding:16px;
  background:rgba(4,4,12,.78);-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);opacity:0;transition:opacity .3s ease}
.ac-hall.on,.ac-result.on{opacity:1}
.ac-box{position:relative;width:min(420px,100%);border-radius:22px;padding:22px 18px 18px;background:linear-gradient(160deg,#1c1934,#0d0c18);
  border:1px solid rgba(255,255,255,.12);box-shadow:0 20px 60px rgba(0,0,0,.6);color:#fff;font-family:var(--v2-ui,system-ui),sans-serif;text-align:center}
.ac-box h2{margin:0 0 14px;font:800 22px/1 var(--v2-display,var(--v2-ui,system-ui)),sans-serif;letter-spacing:.08em;text-transform:uppercase}
.ac-x{position:absolute;top:10px;right:10px;width:40px;height:40px;border-radius:50%;border:0;background:rgba(255,255,255,.08);color:#fff;font-size:16px}
.ac-tiles{display:grid;gap:10px}
.ac-tile{display:flex;align-items:center;gap:12px;min-height:60px;padding:10px 14px;border-radius:14px;border:1px solid rgba(255,255,255,.14);
  background:rgba(255,255,255,.05);color:#fff;text-align:left;font:700 16px/1.2 var(--v2-ui,system-ui),sans-serif}
.ac-tile>b{flex:none;width:32px;height:32px;border-radius:50%;display:grid;place-items:center;background:#ffd36b;color:#1a1305;font-size:15px}
.ac-tile span{flex:1}.ac-tile small{display:block;margin-top:3px;font-weight:500;font-size:12px;color:#b9b3d6}
.ac-tile.done>b{background:#42ebc6}.ac-tile>i{font-style:normal;color:#42ebc6;font-size:20px}
.ac-pay{margin:12px 0 0;color:#ffd36b;font:700 13px/1 var(--v2-ui,system-ui);letter-spacing:.06em}
.ac-row{display:flex;gap:10px;justify-content:center;margin-top:14px}
.ac-row button{min-height:44px;padding:10px 18px;border-radius:999px;border:1px solid rgba(255,255,255,.25);background:rgba(255,255,255,.07);color:#fff;font:700 14px/1 var(--v2-ui,system-ui)}
.ac-result .ac-box p{margin:0;color:#d8d3f0}.ac-result.won h2{color:#42ebc6}
.ac-hold{position:fixed;left:50%;top:max(14%,calc(env(safe-area-inset-top,0px) + 70px));z-index:75;translate:-50% 0;max-width:min(88vw,380px);
  padding:14px 22px 10px;border-radius:18px;background:rgba(10,9,22,.86);border:1px solid rgba(255,211,107,.6);box-shadow:0 0 30px rgba(255,190,90,.35);
  color:#fff;text-align:center;opacity:0;scale:.92;transition:opacity .25s ease,scale .25s ease;pointer-events:none}
.ac-hold.on{opacity:1;scale:1}
.ac-hold p{margin:0;font:800 clamp(18px,5.4vw,24px)/1.15 var(--v2-ui,system-ui),sans-serif}
.ac-hold small{display:block;margin-top:6px;font:600 11px/1 var(--v2-ui,system-ui);letter-spacing:.2em;text-transform:uppercase;color:rgba(255,255,255,.55);animation:acBreath 1.4s ease-in-out infinite}
@keyframes acBreath{50%{opacity:.35}}
.academy-glint{position:relative}
.academy-glint::after{content:"";position:absolute;inset:-3px;border-radius:inherit;pointer-events:none;
  background:conic-gradient(from var(--acg,0deg),transparent 0 80%,rgba(255,224,150,.95) 88%,transparent 96%);
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);-webkit-mask-composite:xor;mask-composite:exclude;padding:2px;animation:acGlint 2.6s linear infinite}
@property --acg{syntax:'<angle>';inherits:false;initial-value:0deg}
@keyframes acGlint{to{--acg:360deg}}
@media (prefers-reduced-motion:reduce){.ac-hold small,.academy-glint::after{animation:none}}
`;
  document.head.appendChild(s);
}
